import { useEffect, useRef } from 'react'
// @ts-expect-error - turf types issue
import * as turf from '@turf/turf'
import type { MapRef } from 'react-map-gl'
import type { Zone } from '../../../../zones/types'
import { convertToGeoJSON } from '../../../utils/mapHelpers'

interface UseZoneViewportParams {
  mapRef: React.MutableRefObject<MapRef | null>
  activeSelectedZone: Zone | null
  editingZoneId: string | null
  getZoneById: (id: string) => Zone | undefined
}

export function useZoneViewport({
  mapRef,
  activeSelectedZone,
  editingZoneId,
  getZoneById,
}: UseZoneViewportParams) {
  const previousSelectedZoneIdRef = useRef<string | null>(null)
  const previousEditingZoneIdRef = useRef<string | null>(null)

  // Pan to selected zone when it changes
  useEffect(() => {
    if (activeSelectedZone?.coordinates?.length && mapRef.current) {
      if (activeSelectedZone.id !== previousSelectedZoneIdRef.current) {
        try {
          const map = mapRef.current.getMap()
          const geoJsonCoords = convertToGeoJSON(activeSelectedZone.coordinates)
          const bbox = turf.bbox(turf.polygon([geoJsonCoords]))

          map.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ] as [mapboxgl.LngLatLike, mapboxgl.LngLatLike],
            {
              padding: 50,
              maxZoom: 15,
              duration: 500,
            }
          )
          previousSelectedZoneIdRef.current = activeSelectedZone.id
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error('Error panning to zone:', error)
        }
      }
    } else if (!activeSelectedZone) {
      previousSelectedZoneIdRef.current = null
    }
  }, [activeSelectedZone, mapRef])

  // Pan to editing zone once when entering edit mode
  useEffect(() => {
    if (
      editingZoneId &&
      mapRef.current &&
      editingZoneId !== previousEditingZoneIdRef.current
    ) {
      const zone = getZoneById(editingZoneId)
      if (zone?.coordinates?.length) {
        try {
          const map = mapRef.current.getMap()
          const geoJsonCoords = convertToGeoJSON(zone.coordinates)
          const bbox = turf.bbox(turf.polygon([geoJsonCoords]))

          map.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ] as [mapboxgl.LngLatLike, mapboxgl.LngLatLike],
            {
              padding: 50,
              maxZoom: 15,
              duration: 500,
            }
          )
          previousEditingZoneIdRef.current = editingZoneId
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error('Error panning to editing zone:', error)
        }
      }
    } else if (!editingZoneId) {
      previousEditingZoneIdRef.current = null
    }
  }, [editingZoneId, getZoneById, mapRef])
}
